<template>
  <div ref="container" :class="['frc-captcha', 'mb-4', {'d-none': invisibleCaptcha}]" />

  <validation-errors v-if="showValidationError" :errors="[validationErrorMessage]" class="mb-1" />
  <submit-button :form-valid="formValid" :label="label" :submitting-label="submittingLabel"
                 :submit-in-progress="disabledSubmit" :class="buttonClass"
                 @click="handleButtonClick" />
</template>

<script>
import SubmitButton from '@/components/buttons/submit-button'
import ValidationErrors from '@/components/inputs/validation-errors'
import { FriendlyCaptchaSDK } from '@friendlycaptcha/sdk'

export default {
  components: { SubmitButton, ValidationErrors },

  props: {
    // This prop needs to be named `buttonClass` rather than `class` because ESLint will
    // throw a vue/no-parsing-error error if a prop is named `class`.
    buttonClass: {
      type: String,
      required: false,
      default: ''
    },
    calculateSolutionOnMount: {
      type: Boolean,
      required: false,
      default: false
    },
    formValid: {
      type: Boolean,
      required: false,
      default: true
    },
    invisibleCaptcha: {
      type: Boolean,
      required: false,
      default: false
    },
    label: {
      type: String,
      required: true
    },
    submitInProgress: {
      type: Boolean,
      required: false,
      default: false
    },
    submittingLabel: {
      type: String,
      required: false
    },
    validationErrors: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  emits: ['error', 'submit-with-token'],

  data() {
    return {
      buttonClicked: false,
      captchaSolution: null,
      executingCaptcha: false
    }
  },

  computed: {
    disabledSubmit() {
      return this.submitInProgress || this.executingCaptcha
    },

    showValidationError() {
      return !!this.validationErrors.friendlyCaptchaVerifyResult
    },

    validationErrorMessage() {
      return this.i18n.t('public.bot_protection.rejected', {org_name: this.agraConfiguration.organisationName})
    }
  },

  watch: {
    submitInProgress() {
      if (!this.submitInProgress) {
        // The submit has finished, and we're still here.
        // This presumably means that the submit _failed_, and we may need to re-submit.
        // Reset the widget so we're ready.
        this.widget.reset()
      }
    }
  },

  mounted() {
    const sdk = new FriendlyCaptchaSDK({
      apiEndpoint: this.agraConfiguration.botProtection.apiEndpointType
    })

    const options = {
      element: this.$refs.container,
      sitekey: this.agraConfiguration.botProtection.siteKey,
      startMode: this.calculateSolutionOnMount ? 'auto' : 'focus',
      language: this.i18n.locale()
    }

    this.widget = sdk.createWidget(options)
    this.widget.addEventListener('frc:widget.requesting', this.captchaStartedCallback)
    this.widget.addEventListener('frc:widget.complete', this.captchaDoneCallback)
    this.widget.addEventListener('frc:widget.error', this.captchaErrorCallback)
  },

  unmounted() {
    this.widget.destroy()
  },

  methods: {
    captchaDoneCallback({ detail }) {
      this.captchaSolution = detail.response
      this.executingCaptcha = false

      if (this.buttonClicked) {
        this.submitAndReset()
      }
    },

    captchaErrorCallback({ detail }) {
      this.executingCaptcha = false

      this.$emit('error', JSON.stringify(detail.error))
    },

    captchaStartedCallback() {
      this.executingCaptcha = true
    },

    handleButtonClick() {
      this.buttonClicked = true

      if (this.captchaSolution) {
        this.submitAndReset()
      } else if (!this.executingCaptcha) {
        // Oops, somehow we did not start the captcha puzzle by the time the button was clicked.
        // Do it now.
        this.widget.start()
      }
    },

    submitAndReset() {
      this.$emit('submit-with-token', {friendlyCaptchaSolution: this.captchaSolution})

      this.buttonClicked = false
      this.captchaSolution = null
    }
  }
}
</script>
